import type { ReactElement } from 'react';
import type { MarketReachView } from '../../../domain/demand/marketViews';
import { formatInt } from '../../format';
import { DarkSectionHeader } from '../common/DarkSectionHeader';
import './MarketPanels.css';

export interface MarketReachPanelProps {
  readonly reach: MarketReachView | null;
}

function score(value: number): string {
  return value.toFixed(2);
}

export function MarketReachPanel({ reach }: MarketReachPanelProps): ReactElement {
  if (reach === null) {
    return (
      <DarkSectionHeader title="Market reach" subtitle="Catchment population by pool">
        <p className="mk-empty">Select an airport to see its catchment.</p>
      </DarkSectionHeader>
    );
  }
  return (
    <DarkSectionHeader
      title={`Market reach: ${reach.airportId}`}
      subtitle="Catchment population by pool"
      summary={`${formatInt(reach.pools.length)} pools`}
      ariaLabel="Market reach"
    >
      <div className="mk-kpis">
        <div className="mk-kpi">
          <div className="mk-kpi-label">Total market reach</div>
          <div className="mk-kpi-value">{formatInt(reach.marketReachPopulation)}</div>
        </div>
        <div className="mk-kpi">
          <div className="mk-kpi-label">Eligible travelers</div>
          <div className="mk-kpi-value">{formatInt(reach.eligibleTravelers)}</div>
        </div>
      </div>
      {reach.pools.length === 0 ? (
        <p className="mk-empty">No catchment pool covers this airport.</p>
      ) : (
        <div className="mk-table-wrap">
          <table className="mk-table">
            <thead>
              <tr>
                <th>Catchment pool</th>
                <th>Population</th>
                <th>GDP / cap</th>
                <th>Tourism dom / intl</th>
                <th>Business dom / intl</th>
                <th>Distance</th>
                <th>Airport share</th>
              </tr>
            </thead>
            <tbody>
              {reach.pools.map((pool) => (
                <tr key={pool.poolId}>
                  <td>{pool.name}</td>
                  <td>{formatInt(pool.population)}</td>
                  <td>${formatInt(pool.gdpPerCapita)}</td>
                  <td>
                    {score(pool.tourismPullDom)} / {score(pool.tourismPullIntl)}
                  </td>
                  <td>
                    {score(pool.businessPullDom)} / {score(pool.businessPullIntl)}
                  </td>
                  <td>{formatInt(pool.distanceKm)} km</td>
                  <td>{pool.sharePct.toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </DarkSectionHeader>
  );
}
